import { useMarkets } from '@api/useMarkets'
import { validateLat, validateLon } from '@components/_SiteWide/CellTemplates/validators'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import {
  BBDTag,
  GraviButton,
  GraviGrid,
  SelectEditor,
  TextCell,
  useGridFetch,
  validateNotEmptyString,
} from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { useQueries } from '@tanstack/react-query'
import api from '@utils/api'
import { getStoreNumberComparator, gridViewManager } from '@utils/grid'
import { Drawer, Select } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { ManyTag } from '../../../_SiteWide/CellTemplates/ManyTag'
import { SupplyZoneDrawer } from '../SupplyZone/SupplyZoneDrawer'
import { terminalCreateConfig } from './createConfig'
import { PrioritizedSelectEditor } from '@components/Admin/Location/Terminal/components/PrioritizedSelectEditor'
import { Tooltip } from 'antd/lib'

const STORAGE_KEY = 'terminal'

export function Terminal() {
  const { rowData, refresh, isLoading } = useGridFetch(api.getLocations)
  const [editableSelectOptions, setEditableSelectOptions] = useState()
  const [showDrawer, setShowDrawer] = useState(false)
  const [supplyZones, setSupplyZones] = useState()
  const { useMarketsQuery } = useMarkets()
  const { data: marketData } = useMarketsQuery()
  const [{ data: locationData }, { data: authorizedCarriersData }, { data: supplyZonesData }] = useQueries({
    queries: [
      { queryKey: ['getLocationQuery'], queryFn: () => api.getLocationMetadata() },
      { queryKey: ['getCarriersQuery'], queryFn: () => api.getCarriers() },
      { queryKey: ['getSupplyZonesQuery'], queryFn: () => api.getSupplyZoneMeta() },
    ],
  })
  const { updateSetting } = useUserSettings()

  const marketNames = useMemo(() => marketData?.rows?.map((row) => row.name), [marketData])
  const supplyNames = useMemo(
    () => (supplyZonesData ? [...supplyZonesData.data?.map((zone) => zone.name), 'N/A'] : ['N/A']),
    [supplyZonesData]
  )
  const locationNames = useMemo(() => locationData?.data?.names?.map((name) => name), [locationData])
  const locationTypes = useMemo(
    () => locationData?.data?.terminal_types?.filter((type) => type !== 'Store' && type !== 'Depot'),
    [locationData]
  )
  useEffect(() => {
    setEditableSelectOptions({
      type: locationTypes ?? [],
      authorized_carriers: authorizedCarriersData ? authorizedCarriersData.data : [],
      supply_zone: supplyNames ?? [],
      split_load_terminals: locationNames ?? [],
      market_names: marketNames ?? [],
    })
    setSupplyZones(supplyZonesData?.data)
  }, [marketData, locationData, supplyZonesData, authorizedCarriersData])

  useEffect(() => {
    if (supplyZones) {
      setEditableSelectOptions((selectOptions) => ({
        ...selectOptions,
        supply_zone: supplyZones.map((zone) => zone.name),
      }))
    }
  }, [supplyZones])

  const { scopes } = useSecurityContext()
  const canEdit = scopes.admin?.location_write

  const columnDefs = useMemo(() => getColumnDefs(editableSelectOptions, canEdit), [editableSelectOptions])
  const getRowId = useCallback((row) => row?.data?.id, [])
  const agPropOverrides = useMemo(
    () => ({
      frameworkComponents: {
        textCell: TextCell,
        select: SelectEditor,
        activeCR: ActiveCR,
      },
      rowHeight: 75,
      getRowId,
    }),
    []
  )

  return (
    <>
      <Drawer
        title='Manage Supply Zones'
        placement='right'
        width='500'
        closable
        onClose={() => setShowDrawer(false)}
        open={showDrawer}
        rootClassName='supply-zone-drawer'
        destroyOnClose
      >
        <SupplyZoneDrawer
          closeDrawer={() => setShowDrawer(false)}
          supplyZones={supplyZones}
          setSupplyZones={setSupplyZones}
        />
      </Drawer>
      <GraviGrid
        storageKey={STORAGE_KEY}
        rowData={rowData}
        columnDefs={columnDefs}
        updateEP={canEdit && api.updateLocation}
        loading={isLoading}
        createEP={canEdit && api.createLocation}
        createConfig={terminalCreateConfig}
        createSelectOptions={editableSelectOptions}
        shouldInsertCreated
        agPropOverrides={agPropOverrides}
        controlBarProps={{
          title: 'Terminal Management',
          actionButtons: (
            <>
              <UploadDownloadButtons
                downloadEP={api.downloadLocations}
                uploadEP={canEdit && api.uploadLocations}
                fileName='Terminals.xlsx'
                refreshCallback={refresh}
              />
              {canEdit && (
                <GraviButton
                  appearance='outlined'
                  theme2
                  buttonText='Manage Supply Zones'
                  onClick={() => setShowDrawer(!showDrawer)}
                />
              )}
            </>
          ),
        }}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
    </>
  )
}

const getColumnDefs = (editableSelectOptions, canEdit) => [
  {
    field: 'name',
    filter: true,
    flex: 2.5,
    cellRenderer: ({ value }) => <TextCell value={value} weight='bold' />,
    valueSetter: validateNotEmptyString,
  },
  {
    field: 'short_name',
    headerName: 'Short Name',
    flex: 1.5,
    valueSetter: validateNotEmptyString,
  },
  {
    field: 'tcn',
    headerName: 'TCN',
    filter: true,
    flex: 1.5,
    editable: canEdit,
  },
  {
    headerName: 'Active',
    field: 'active',
    flex: 1,
    cellEditor: 'agRichSelectCellEditor',
    cellEditorPopup: true,
    valueGetter: (params) => (params?.data?.active ? 'Yes' : 'No'),
    valueSetter: (params) => {
      params.data.active = params.newValue === 'Yes'
      return true
    },
    cellEditorParams: {
      values: ['Yes', 'No'],
      cellHeight: 50,
    },
  },
  {
    field: 'address',
    flex: 3,
    cellRenderer: 'textCell',
    cellRendererParams: { toUpperCase: true },
  },
  {
    field: 'city',
    filter: true,
    flex: 2,
    cellRenderer: 'textCell',
    cellRendererParams: { toUpperCase: true },
  },
  {
    field: 'state',
    filter: true,
    cellRenderer: 'textCell',
    cellRendererParams: { toUpperCase: true },
  },
  {
    headerName: 'Postal Code',
    field: 'postal_code',
    filter: true,
    comparator: getStoreNumberComparator('postal_code'),
  },
  {
    field: 'type',
    filter: true,
    cellEditor: 'agRichSelectCellEditor',
    cellEditorPopup: true,
    cellEditorParams: {
      values: editableSelectOptions && editableSelectOptions.type,
    },
  },
  {
    field: 'split_load_terminals',
    headerName: 'Split Load Terminals',
    cellRenderer: ({ value }) => <ManyTag tagItems={value} maxCount={2} />,
    cellEditor: 'select',
    filter: true,
    cellEditorParams: {
      options: editableSelectOptions?.split_load_terminals ?? [],
      placeholder: 'Select Terminals',
      mode: 'multiple',
    },
    flex: 2,
  },
  {
    field: 'supply_zones',
    headerName: 'Supply Zones',
    flex: 2,
    cellEditor: PrioritizedSelectEditor,
    cellEditorPopup: true,
    filter: true,
    valueFormatter: ({ value }) => {
      return value?.length > 0 ? value : 'N/A'
    },
    cellEditorParams: {
      options: editableSelectOptions && editableSelectOptions.supply_zone,
      mode: 'multiple',
      placeholder: 'Select Supply Zones',
    },
    cellRenderer: ({ value }) => {
      if (!value?.length) return 'N/A'
      // add star to front of first supply zone
      const values = value.map((zone) => (zone === value[0] ? `★ ${zone}` : zone))
      return (
        <div style={{ flexWrap: 'wrap' }} className='flex items-center'>
          <Tooltip title='Primary Supply Zone'>
            <BBDTag className='text-ellipsis'>{values[0]}</BBDTag>
          </Tooltip>
          <ManyTag tagItems={values.slice(1)} maxCount={2} />
        </div>
      )
    },
  },
  {
    field: 'lat',
    sortable: false,
    cellClass: 'right-align-cell',
    flex: 2,
    valueSetter: validateLat,
    type: 'rightAligned',
  },
  {
    field: 'lon',
    sortable: false,
    cellClass: 'right-align-cell',
    type: 'rightAligned',
    flex: 2,
    valueSetter: validateLon,
  },
  {
    field: 'authorized_carriers',
    headerName: 'Authorized Carriers',
    cellEditor: 'select',
    flex: 3,
    filter: true,
    // cellEditorPopup: true,
    cellEditorParams: {
      options: editableSelectOptions && editableSelectOptions.authorized_carriers,
      placeholder: 'Select Carriers',
      mode: 'multiple',
    },
    cellRenderer: ({ value }) => <ManyTag tagItems={value} maxCount={2} />,
  },
  {
    field: 'market',
    editable: canEdit,
    width: 100,
    filter: true,
    cellEditor: 'agRichSelectCellEditor',
    cellEditorPopup: true,
    cellEditorParams: {
      values: editableSelectOptions && editableSelectOptions.market_names,
    },
  },
  {
    field: 'geofence',
    editable: false,
    headerTooltip:
      'Use a tool like https://geojson.io to define a polygon and copy paste the list of coordinates into the loader. The coordinates are in the longitude, latitude format. Will use the default terminal radius if left blank.',
    valueFormatter: ({ value }) => (value === null || (Array.isArray(value) && value.length === 0) ? null : 'Polygon'),
  },
]

function ActiveCR({ value }) {
  return <TextCell value={value} weight='bold' />
}
